// @flow

import type {Node} from '@markdoc/markdoc';
import {Tag} from 'docs/util';
import type {MarkdocTagRegistration} from 'docs/markdoc';
import type {ValidationMetadata} from '../../../../../manifest/validators/getValidationConfig';

type PartialType = {|
  text: string,
  globalVariables: {},
  content: Node,
  contexts: {[string]: {[string]: any}},
|};

const validatePartial = (node, config) => {
  if (!node.attributes?.file || !config) {
    return [];
  }
  const validationMetadata: ValidationMetadata = config.validationMetadata;

  /*
    At present, we don't include any validationMetadata
    at render time, just during the manifest build process. That will
    probably change in the future, but for now we need to make sure
    that this link check is skipped when config.validationMetadata is undefined, which is
    always going to be the case when validation is invoked outside of
    the manifest builder.
    */
  if (!validationMetadata) {
    return [];
  }

  const file: string = (node.attributes?.file: any);

  if (typeof file !== 'string') {
    return [
      {
        id: 'invalid-partial-file-type',
        level: 'critical',
        message: `Partial file names must be a string`,
      },
    ];
  }

  if (validationMetadata.partials) {
    if (!validationMetadata.partials.includes(node.attributes.file)) {
      return [
        {
          id: 'invalid-partial-file',
          level: 'critical',
          message: `The partial ${file} is not a valid partial.`,
        },
      ];
    }
  }

  return [];
};

export const Partial: MarkdocTagRegistration = {
  tag: 'partial',
  component: 'Partial',
  selfClosing: true,
  attributes: {
    file: {
      type: String,
      render: false,
      required: true,
      description:
        'The path to the markdoc file relative to the /docs/content directory.',
      matches(config) {
        return config?.validationMetadata?.partials;
      },
    },
    variables: {
      type: Object,
      render: false,
      description:
        'An object that contains the variables to be passed through to the partials.',
    },
  },
  validate: validatePartial,
  render(node, config) {
    const {file, variables} = node.attributes;
    const partial: PartialType = (config.partials[file]: any);

    if (!partial) {
      return [];
    }

    const scopedConfig = {
      ...config,
      variables: {
        ...config.variables,
        ...partial.contexts,
        ...partial.globalVariables,
        ...variables,
        intl: config.variables.intl,
        ['$$partial:filename']: file,
      },
    };

    const renderChildren = (part) =>
      part.resolve(scopedConfig).renderChildren(scopedConfig);

    const children = Array.isArray(partial.content)
      ? partial.content.flatMap(renderChildren)
      : renderChildren(partial.content);

    return new Tag(this.tag, node.attributes, children);
  },
};
